import type { Board, Piece, PieceCounts, PlayerNumber } from './types';

export const BOARD_SIZE = 8;

const PIECES = new Set<Piece>([0, 1, 2, 3, 4]);

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_SIZE }, () => Array<Piece>(BOARD_SIZE).fill(0));
}

export function createInitialBoard(): Board {
  const board = createEmptyBoard();

  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (isDarkSquare(row, col)) {
        board[row][col] = 1;
      }
    }
  }

  for (let row = 5; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (isDarkSquare(row, col)) {
        board[row][col] = 2;
      }
    }
  }

  return board;
}

export function cloneBoard(board: Board): Board {
  return board.map((row) => [...row]);
}

export function parseBoard(value: unknown): Board {
  if (Array.isArray(value)) {
    return normalizeBoard(value);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? normalizeBoard(parsed) : createEmptyBoard();
    } catch {
      return createEmptyBoard();
    }
  }

  return createEmptyBoard();
}

export function isInsideBoard(row: number, col: number): boolean {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;
}

export function isDarkSquare(row: number, col: number): boolean {
  return (row + col) % 2 === 1;
}

export function countPieces(board: Board): PieceCounts {
  return board.reduce<PieceCounts>(
    (counts, row) => {
      row.forEach((piece) => {
        if (piece === 1 || piece === 3) counts.player1++;
        if (piece === 2 || piece === 4) counts.player2++;
      });
      return counts;
    },
    { player1: 0, player2: 0 }
  );
}

export function getWinnerByPieces(board: Board): PlayerNumber | null {
  const counts = countPieces(board);

  if (counts.player1 === 0) return 2;
  if (counts.player2 === 0) return 1;

  return null;
}

export function getRealBoardPosition(row: number, col: number): string {
  const file = String.fromCharCode(97 + (BOARD_SIZE - 1 - col));
  const rank = row + 1;
  return `${file}${rank}`;
}

function normalizeBoard(rows: unknown[]): Board {
  const board = createEmptyBoard();

  rows.slice(0, BOARD_SIZE).forEach((rowValue, rowIndex) => {
    if (!Array.isArray(rowValue)) return;

    rowValue.slice(0, BOARD_SIZE).forEach((pieceValue, colIndex) => {
      board[rowIndex][colIndex] = normalizePiece(pieceValue);
    });
  });

  return board;
}

function normalizePiece(value: unknown): Piece {
  const piece = Number(value) as Piece;
  return PIECES.has(piece) ? piece : 0;
}

